import { createAsyncThunk } from "@reduxjs/toolkit";
import { AppDispatch, RootState } from "../store";
import { 
  setNotifications, 
  addNotification, 
  removeNotification, 
  markNotificationAsRead, 
  markAllNotificationsAsRead, 
  clearAllNotifications,
  setNotificationMute
} from "../actions/notifications";
import { getInstanceAlias, persistMultiInstanceNotifications } from "../utils/utils";

/**
 * Initializes the notifications for the agent.
 * This function sets up the initial state of the notifications from localStorage.
 * It is typically called when the agent logs in or the instance is switched.
 *
 * @param {string} agentId - The ID of the agent whose notifications are being initialized.
 * @returns {void}
 */
export const initializeNotifications = createAsyncThunk<
  void,
  null,
  { dispatch: AppDispatch; state: RootState }
>("notifications/initializeNotifications", (_ignore, thunkApi) => {
  const state: RootState = thunkApi.getState();
  const agent_id = state.agent.id as string;
  const agentNotificationData = localStorage.getItem("agent_notifications");
  
  console.log("Initializing notifications for agent:", agent_id);
  console.log("Saved notification data:", agentNotificationData);
  
  if (agentNotificationData !== null) {
    const activeInstance = getInstanceAlias();
    const agentSavedData = JSON.parse(agentNotificationData);
    const savedNotifications = agentSavedData[agent_id]?.[activeInstance];
    
    console.log("Active instance:", activeInstance);
    console.log("Saved notifications:", savedNotifications);
    
    if (savedNotifications) {
      console.log("Loading notifications:", savedNotifications.notifications);
      // Restore the notifications
      thunkApi.dispatch(setNotifications(savedNotifications.notifications || []));
      // Restore the mute state
      thunkApi.dispatch(setNotificationMute(savedNotifications.muteNotifications || false));
    }
  }
});

/**
 * Action to add a notification with persistence.
 * This function dispatches an action to update the state with a new notification entry
 * and persists the updated state to localStorage.
 *
 * @param {Partial<Notification>} notification - The notification object to be added.
 * @returns {void}
 */
export const addNotificationAction = createAsyncThunk<
  void,
  any,
  { dispatch: AppDispatch; state: RootState }
>("notifications/addNotificationAction", (notification, thunkApi) => {
  thunkApi.dispatch(addNotification(notification));
  
  // Persist notifications to localStorage
  persistMultiInstanceNotifications(thunkApi.getState());
});

/**
 * Action to remove a notification with persistence.
 * This function dispatches an action to remove a notification from the state
 * and persists the updated state to localStorage.
 *
 * @param {string} id - The ID of the notification to be removed.
 * @returns {void}
 */
export const removeNotificationAction = createAsyncThunk<
  void,
  string,
  { dispatch: AppDispatch; state: RootState }
>("notifications/removeNotificationAction", (id, thunkApi) => {
  thunkApi.dispatch(removeNotification(id));
  
  // Persist notifications to localStorage
  persistMultiInstanceNotifications(thunkApi.getState());
});

/**
 * Action to mark a notification as read with persistence.
 * This function dispatches an action to mark a notification as read
 * and persists the updated state to localStorage.
 *
 * @param {string} id - The ID of the notification to be marked as read.
 * @returns {void}
 */
export const markNotificationAsReadAction = createAsyncThunk<
  void,
  string,
  { dispatch: AppDispatch; state: RootState }
>("notifications/markNotificationAsReadAction", (id, thunkApi) => {
  thunkApi.dispatch(markNotificationAsRead(id));
  
  // Persist notifications to localStorage
  persistMultiInstanceNotifications(thunkApi.getState());
});

/**
 * Action to mark all notifications as read with persistence.
 * This function dispatches an action to mark all notifications as read
 * and persists the updated state to localStorage.
 *
 * @returns {void}
 */
export const markAllNotificationsAsReadAction = createAsyncThunk<
  void,
  null,
  { dispatch: AppDispatch; state: RootState }
>("notifications/markAllNotificationsAsReadAction", (_ignore, thunkApi) => {
  thunkApi.dispatch(markAllNotificationsAsRead());
  
  // Persist notifications to localStorage
  persistMultiInstanceNotifications(thunkApi.getState());
});

/**
 * Action to clear all notifications with persistence.
 * This function dispatches an action to clear all notifications from the state
 * and persists the updated state to localStorage.
 *
 * @returns {void}
 */
export const clearAllNotificationsAction = createAsyncThunk<
  void,
  null,
  { dispatch: AppDispatch; state: RootState }
>("notifications/clearAllNotificationsAction", (_ignore, thunkApi) => {
  thunkApi.dispatch(clearAllNotifications());
  
  // Persist notifications to localStorage
  persistMultiInstanceNotifications(thunkApi.getState());
});
